'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { gql } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../../hooks/useToast';
import { FlattenedUser } from '../../../types/user';
import { UserType } from '../../../types/generated';
import {
	useClientOnboardingUseFinishUp_GetClientUserWhereQuery,
	useClientOnboardingFinishUp_UpdateClientUserMutation,
	useClientOnboardingFinishUp_MarkUserOnboardingCompleteMutation,
} from '#types';
import dateIsValid from '../../../lib/helpers/dateIsValid';
import validations from '../../../lib/helpers/validations';
import { updateUser } from '../../../redux/actions';

const clientOnboardingFinishUpClientUserFragment = gql`
	fragment clientOnboardingFinishUpClientUser on ClientUser {
		id
		venue
		weddingDate
		phone
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getClientUserWhere = gql`
	query ClientOnboardingUseFinishUp_getClientUserWhere(
		$where: ClientUserWhereUniqueInput!
	) {
		getClientUserWhere(where: $where) {
			...clientOnboardingFinishUpClientUser
		}
	}
	${ clientOnboardingFinishUpClientUserFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const updateClientUserMutation = gql`
	mutation clientOnboardingFinishUp_updateClientUser(
		$data: ClientUserUpdateCustomInput!
		$where: ClientUserWhereUniqueInput!
	) {
		updateClientUser(data: $data, where: $where) {
			...clientOnboardingFinishUpClientUser
		}
	}
	${ clientOnboardingFinishUpClientUserFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const markUserOnboardingCompleteMutation = gql`
	mutation clientOnboardingFinishUp_markUserOnboardingComplete {
		markUserOnboardingComplete
	}
`;

export const useFinishUp = () => {
	const { showError } = useToast();
	const dispatch = useDispatch();
	const user: FlattenedUser = useSelector( ( state: any ) => state.user );
	const loggedInClientUser =
		user?.isLoggedIn && user.userType === UserType.ClientUser
			? user
			: undefined;
	const { data: clientUserData, error: clientUserError } =
		useClientOnboardingUseFinishUp_GetClientUserWhereQuery( {
			variables: {
				where: {
					id: loggedInClientUser?.id || '',
				},
			},
			skip: !loggedInClientUser?.id,
		} );
	const [ weddingDate, setWeddingDate ] = useState(
		clientUserData?.getClientUserWhere?.weddingDate
			? clientUserData?.getClientUserWhere?.weddingDate
			: undefined
	);
	const [ weddingDateValidation, setWeddingDateValidation ] = useState<
		string | undefined
	>();
	const [ venue, setVenue ] = useState(
		clientUserData?.getClientUserWhere?.venue
			? clientUserData?.getClientUserWhere?.venue
			: undefined
	);
	const [ venueValidation, setVenueValidation ] = useState<string | undefined>();
	const [ phone, setPhone ] = useState(
		clientUserData?.getClientUserWhere?.phone
			? clientUserData?.getClientUserWhere?.phone
			: undefined
	);
	const [ phoneValidation, setPhoneValidation ] = useState<string | undefined>();
	const onBlurValidationEnabledRef = useRef( false );
	const [ updateClientUser ] =
		useClientOnboardingFinishUp_UpdateClientUserMutation();
	const [ markUserOnboardingComplete ] =
		useClientOnboardingFinishUp_MarkUserOnboardingCompleteMutation();
	useEffect( () => {
		if ( clientUserError ) {
			showError( clientUserError.message );
		} else if ( clientUserData ) {
			if ( clientUserData.getClientUserWhere?.weddingDate ) {
				setWeddingDate( clientUserData.getClientUserWhere?.weddingDate );
			}
			if ( clientUserData.getClientUserWhere?.venue ) {
				setVenue( clientUserData.getClientUserWhere?.venue );
			}
			if ( clientUserData.getClientUserWhere?.phone ) {
				setPhone( clientUserData.getClientUserWhere?.phone );
			}
		}
	}, [
		clientUserError,
		clientUserData,
		showError
	] );

	const onValidate = useCallback(
		( force = false ) => {
			if ( !force && !onBlurValidationEnabledRef.current ) {
				return true;
			}
			onBlurValidationEnabledRef.current = true;
			const weddingDateError =
				weddingDate && !dateIsValid( weddingDate ) ? 'Invalid date.' : undefined;
			const venueError =
				venue && venue.length > 255
					? 'Venue must be less than 255 characters'
					: undefined;
			const phoneError = phone ? validations.validatePhone( phone ) : undefined;
			setWeddingDateValidation( weddingDateError );
			setVenueValidation( venueError );
			setPhoneValidation( phoneError );
			if ( weddingDateError || venueError || phoneError ) {
				return false;
			}
			return true;
		},
		[
			weddingDate,
			venue,
			phone
		]
	);

	const onNext = useCallback( async () => {
		onBlurValidationEnabledRef.current = true;
		const isValid = onValidate();
		if ( !isValid ) {
			return false;
		}

		const res = await updateClientUser( {
			variables: {
				data: {
					weddingDate,
					venue,
					phone,
				},
				where: {
					id: loggedInClientUser?.id || '',
				},
			},
		} );
		if ( res.errors || !res.data?.updateClientUser ) {
			res.errors?.forEach( ( error ) => showError( error ) );
			return false;
		}

		// Update Redux with the new user data
		dispatch(
			updateUser( {
				weddingDate,
				venue,
				phone,
			} )
		);

		await markUserOnboardingComplete();
		return true;
	}, [
		onValidate,
		weddingDate,
		venue,
		phone,
		updateClientUser,
		loggedInClientUser,
		markUserOnboardingComplete,
		dispatch,
		showError,
	] );

	const onBack = useCallback( () => {
		onBlurValidationEnabledRef.current = true;
		const isValid = onValidate( true );
		if ( !isValid ) {
			return false;
		}
		onBlurValidationEnabledRef.current = false;
		return true;
	}, [ onValidate ] );

	return {
		weddingDate,
		setWeddingDate,
		weddingDateValidation,
		venue,
		setVenue,
		venueValidation,
		phone,
		setPhone,
		phoneValidation,
		onNext,
		onValidate,
		onBack,
	};
};
